import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Lock, LogOut, Save, Plus, Trash2, Upload, 
  FileText, Users, Settings, ChevronDown,
  Globe, Video, Eye, Layout, BookOpen, FileEdit,
  FileStack, GripVertical, ArrowUp, ArrowDown, ExternalLink,
  Image, HelpCircle, Megaphone, Youtube, X, Calendar, Check, Clock, Link2,
  Layers, Package, Star, DollarSign
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token Management - SIMPLE AND RELIABLE
const TOKEN_KEY = 'admin_token';
let memoryToken = null; // Backup in memory

const getToken = () => {
  if (memoryToken) return memoryToken;
  try {
    const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    if (token) memoryToken = token;
    return token;
  } catch (e) {
    return memoryToken;
  }
};

const setToken = (token) => {
  memoryToken = token;
  try { localStorage.setItem(TOKEN_KEY, token); } catch (e) {}
  try { sessionStorage.setItem(TOKEN_KEY, token); } catch (e) {}
};

const removeToken = () => {
  memoryToken = null;
  try { localStorage.removeItem(TOKEN_KEY); } catch (e) {}
  try { sessionStorage.removeItem(TOKEN_KEY); } catch (e) {}
};

const authHeaders = () => {
  const token = getToken();
  const config = { withCredentials: true };
  if (token) config.headers = { Authorization: `Bearer ${token}` };
  return config;
};

// OAuth Callback Handler
const AuthCallback = ({ onLogin }) => {
  const [status, setStatus] = useState('Authentifizierung läuft...');
  const [error, setError] = useState('');

  useEffect(() => {
    const processSession = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error('No session_id found in hash:', hash);
        setError('Keine Session gefunden');
        setTimeout(() => { window.location.href = '/admin'; }, 2000);
        return;
      }

      const sessionId = sessionIdMatch[1];
      setStatus('Session wird validiert...');
      
      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
        
        if (response.data.status === 'success') {
          // IMPORTANT: Store the JWT token from Google login
          if (response.data.access_token) {
            setToken(response.data.access_token);
          }
          
          setStatus(`Willkommen, ${response.data.user.name}!`);
          // Clear hash first
          window.history.replaceState(null, '', '/admin');
          // Then trigger login after a small delay
          setTimeout(() => {
            onLogin(response.data.user);
          }, 500);
        } else {
          setError('Login fehlgeschlagen');
          setTimeout(() => { window.location.href = '/admin'; }, 2000);
        }
      } catch (err) {
        console.error('Session validation error:', err);
        setError(err.response?.data?.detail || 'Login fehlgeschlagen');
        setTimeout(() => { window.location.href = '/admin'; }, 2000);
      }
    };

    processSession();
  }, [onLogin]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="text-white text-center">
        {!error && <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4" />}
        {error ? (
          <p className="text-red-400">{error}</p>
        ) : (
          <p>{status}</p>
        )}
      </div>
    </div>
  );
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const token = res.data.access_token;
      setToken(token);
      toast.success('Login erfolgreich!');
      onLogin();
    } catch (err) {
      toast.error('Falsches Passwort');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      {/* Background Orbs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="glow-orb w-96 h-96 bg-purple-600/20" style={{ top: '10%', right: '20%' }} />
        <div className="glow-orb w-80 h-80 bg-blue-600/20" style={{ bottom: '20%', left: '10%' }} />
      </div>
      <div className="w-full max-w-md relative z-10">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 shadow-glow">
          <Lock className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white text-center mb-6">Admin Login</h1>
          
          <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 border border-slate-600 text-white py-3 px-4 rounded-xl hover:bg-slate-700 hover:border-purple-500/50 transition-all duration-300 mb-4">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Mit Google anmelden
          </button>
          
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-700" /></div>
            <div className="relative flex justify-center text-sm"><span className="px-2 bg-slate-800 text-slate-500">oder</span></div>
          </div>

          {!showPassword ? (
            <button onClick={() => setShowPassword(true)} className="w-full text-slate-400 hover:text-purple-400 py-2 text-sm transition-colors">
              Mit Passwort anmelden (Backup)
            </button>
          ) : (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin Passwort" className="input-dark" required />
              <Button type="submit" disabled={loading} className="btn-glow w-full py-3">
                {loading ? 'Lädt...' : 'Anmelden'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Section Card Component
const SectionCard = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-700 rounded-xl overflow-hidden bg-slate-800">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-4 py-3 flex items-center justify-between bg-slate-800 hover:bg-slate-700 transition-colors">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-purple-400" />}
          <span className="font-medium text-white">{title}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="p-4 border-t border-slate-700">{children}</div>}
    </div>
  );
};

// Image Upload Component - SIMPLIFIED
const ImageUpload = ({ value, onChange, label }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleUpload = async (e) => {
    const fileInput = e.target;
    const file = fileInput.files?.[0];
    if (!file) return;
    
    setError('');
    
    if (file.size > 10 * 1024 * 1024) {
      setError('Datei zu groß (max 10MB)');
      fileInput.value = '';
      return;
    }
    
    setUploading(true);
    
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API}/admin/upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Upload fehlgeschlagen');
      }
      
      const data = await response.json();
      onChange(data.url);
      setError('');
    } catch (err) {
      console.error('Upload error:', err);
      setError(typeof err === 'object' ? (err.message || 'Upload fehlgeschlagen') : 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      fileInput.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {label && <label className="text-sm text-slate-400">{label}</label>}
      <div className="flex gap-2">
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="input-dark text-sm flex-1" placeholder="URL" />
        <label className="cursor-pointer">
          <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          <div className="btn-glow px-3 py-2 rounded-md flex items-center text-sm">
            {uploading ? '...' : <Upload className="w-4 h-4" />}
          </div>
        </label>
      </div>
      {error && <p className="text-red-400 text-xs">{error}</p>}
      {value && <img src={value} alt="Preview" className="w-16 h-16 object-cover rounded-lg mt-1 border border-slate-700" />}
    </div>
  );
};

// Reference Categories
const REFERENCE_CATEGORIES = ['CGI Videos', '2D Animations', 'Video Productions', '3D Animations', 'Graphics', 'Photos', 'Stories'];

// Admin Dashboard
const AdminDashboard = ({ onLogout, user }) => {
  const [activeTab, setActiveTab] = useState('content');
  const [content, setContent] = useState({});
  const [logos, setLogos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [social, setSocial] = useState([]);
  const [references, setReferences] = useState([]);
  const [services, setServices] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [pages, setPages] = useState([]);
  const [pagesList, setPagesList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState(null);
  
  // Demo booking states
  const [demoRequests, setDemoRequests] = useState([]);
  const [demoApps, setDemoApps] = useState([]);
  const [editingDemoRequest, setEditingDemoRequest] = useState(null);
  const [newDemoApp, setNewDemoApp] = useState({ name: '', description: '', image: '', order: 0, active: true });
  
  // Products states
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '', slug: '', tagline: '', short_description: '', description: '',
    category: '', icon: 'layers', image: '', features: [], benefits: [],
    price: '', is_popular: false, active: true
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, logosRes, projectsRes, socialRes, refsRes, servicesRes, subsRes, pagesRes] = await Promise.all([
          axios.get(`${API}/content`),
          axios.get(`${API}/logos`),
          axios.get(`${API}/projects`),
          axios.get(`${API}/social`),
          axios.get(`${API}/references`),
          axios.get(`${API}/services`),
          axios.get(`${API}/admin/submissions`, authHeaders()).catch(() => ({ data: [] })),
          axios.get(`${API}/pages`).catch(() => ({ data: [] }))
        ]);
        setContent(contentRes.data);
        setLogos(logosRes.data);
        setProjects(projectsRes.data);
        setSocial(socialRes.data);
        setReferences(refsRes.data);
        setServices(servicesRes.data);
        setSubmissions(subsRes.data);
        setPages(pagesRes.data);
        setPagesList(pagesRes.data.map(p => ({ id: p.id, title: p.title, slug: p.slug })));
        
        // Fetch demo data
        fetchDemoData();
        // Fetch products data
        fetchProducts();
      } catch (err) {
        toast.error('Fehler beim Laden');
      }
    };
    fetchData();
  }, []);

  // Fetch products
  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/products`, authHeaders());
      setProducts(res.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  // Fetch demo requests and apps
  const fetchDemoData = async () => {
    try {
      const [requestsRes, appsRes] = await Promise.all([
        axios.get(`${API}/admin/demo/requests`, authHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/demo/apps`, authHeaders()).catch(() => ({ data: [] }))
      ]);
      setDemoRequests(requestsRes.data);
      setDemoApps(appsRes.data);
    } catch (err) {
      console.error('Error fetching demo data:', err);
    }
  };

  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });

  const saveContent = async () => {
    setSaving(true);
    setSaveStatus({ type: '', message: '' });
    try {
      await axios.put(`${API}/admin/content`, content, authHeaders());
      setSaveStatus({ type: 'success', message: 'Gespeichert!' });
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus({ type: 'error', message: 'Fehler beim Speichern' });
    } finally {
      setSaving(false);
    }
  };

  // Logo functions
  const addLogo = () => setLogos([...logos, { id: `new-${Date.now()}`, name: '', url: '', make_black: true }]);
  const updateLogo = (id, field, value) => setLogos(logos.map(l => l.id === id ? {...l, [field]: value} : l));
  const saveLogo = async (logo) => {
    try {
      if (logo.id.startsWith('new-')) {
        const res = await axios.post(`${API}/admin/logos`, logo, authHeaders());
        setLogos(logos.map(l => l.id === logo.id ? res.data : l));
      } else {
        await axios.put(`${API}/admin/logos/${logo.id}`, logo, authHeaders());
      }
      toast.success('Logo gespeichert!');
    } catch { toast.error('Fehler'); }
  };
  const deleteLogo = async (id) => {
    try {
      // Always try to delete from backend - even 'new-' IDs might be in DB from bulk upload
      try {
        await axios.delete(`${API}/admin/logos/${id}`, authHeaders());
      } catch (err) {
        // Ignore 404 errors for truly new logos
        if (err.response?.status !== 404) throw err;
      }
      setLogos(logos.filter(l => l.id !== id));
      toast.success('Gelöscht!');
    } catch { toast.error('Fehler'); }
  };
  
  // Bulk upload logos
  const [bulkUploading, setBulkUploading] = useState(false);
  const handleBulkLogoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setBulkUploading(true);
    const newLogos = [];
    
    for (const file of files) {
      try {
        const token = getToken();
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API}/admin/upload`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          // Extract name from filename (without extension)
          const name = file.name.replace(/\.[^/.]+$/, '');
          const newLogo = {
            id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: name,
            url: data.url,
            make_black: true
          };
          newLogos.push(newLogo);
        }
      } catch (err) {
        console.error('Upload error for', file.name, err);
      }
    }
    
    if (newLogos.length > 0) {
      // Save all logos to backend
      for (const logo of newLogos) {
        try {
          const res = await axios.post(`${API}/admin/logos`, logo, authHeaders());
          logo.id = res.data.id; // Update with real ID
        } catch (err) {
          console.error('Save error for', logo.name, err);
        }
      }
      setLogos([...logos, ...newLogos]);
      toast.success(`${newLogos.length} Logos hochgeladen!`);
    }
    
    setBulkUploading(false);
    e.target.value = '';
  };

  // Project functions
  const addProject = () => setProjects([...projects, { id: `new-${Date.now()}`, title: '', category: '', image: '', url: '', linked_page_id: null }]);
  const updateProject = (id, field, value) => setProjects(projects.map(p => p.id === id ? {...p, [field]: value} : p));
  const saveProject = async (project) => {
    try {
      if (project.id.startsWith('new-')) {
        const res = await axios.post(`${API}/admin/projects`, project, authHeaders());
        setProjects(projects.map(p => p.id === project.id ? res.data : p));
      } else {
        await axios.put(`${API}/admin/projects/${project.id}`, project, authHeaders());
      }
      toast.success('Projekt gespeichert!');
    } catch { toast.error('Fehler'); }
  };
  const deleteProject = async (id) => {
    try {
      if (!id.startsWith('new-')) await axios.delete(`${API}/admin/projects/${id}`, authHeaders());
      setProjects(projects.filter(p => p.id !== id));
      toast.success('Gelöscht!');
    } catch { toast.error('Fehler'); }
  };

  // Page functions
  const addPage = () => {
    const newPageId = `new-${Date.now()}`;
    const newPage = { 
      id: newPageId, 
      title: 'Neue Seite', 
      slug: '', 
      blocks: [],
      seo_title: '',
      seo_description: '',
      seo_image: ''
    };
    setPages([...pages, newPage]);
    setSelectedPageId(newPageId); // Auto-select new page
  };
  const updatePage = (id, field, value) => setPages(pages.map(p => p.id === id ? {...p, [field]: value} : p));
  
  // Block type definitions
  const BLOCK_TYPES = [
    { id: 'image_text', label: 'Bild + Text', icon: Image, description: 'Klassischer Block mit Bild und Text' },
    { id: 'single_image', label: 'Einzelbild', icon: Image, description: 'Großes Bild mit optionalem Titel' },
    { id: 'video', label: 'YouTube Video', icon: Youtube, description: 'Eingebettetes YouTube Video' },
    { id: 'gallery', label: 'Galerie', icon: Layout, description: 'Bildergalerie mit mehreren Bildern' },
    { id: 'faq', label: 'FAQ', icon: HelpCircle, description: 'Häufig gestellte Fragen' },
    { id: 'cta', label: 'Call to Action', icon: Megaphone, description: 'Volle Breite Banner mit Button' },
  ];
  
  const [showBlockTypeMenu, setShowBlockTypeMenu] = useState(null); // pageId or null
  
  const addBlockToPage = (pageId, blockType = 'image_text') => {
    setShowBlockTypeMenu(null);
    setPages(pages.map(p => {
      if (p.id === pageId) {
        const newOrder = p.blocks.length;
        const baseBlock = { 
          id: `block-${Date.now()}`, 
          type: blockType,
          title: '', 
          text: '', 
          order: newOrder,
          image: '',
          video_url: '',
          gallery_images: [],
          faq_items: [],
          cta_button_text: 'Get in Touch',
          cta_button_link: '/#contact'
        };
        return {...p, blocks: [...p.blocks, baseBlock]};
      }
      return p;
    }));
  };
  
  // FAQ item management
  const addFaqItem = (pageId, blockId) => {
    setPages(pages.map(p => {
      if (p.id === pageId) {
        return {...p, blocks: p.blocks.map(b => {
          if (b.id === blockId) {
            const items = b.faq_items || [];
            return {...b, faq_items: [...items, { question: '', answer: '' }]};
          }
          return b;
        })};
      }
      return p;
    }));
  };
  
  const updateFaqItem = (pageId, blockId, index, field, value) => {
    setPages(pages.map(p => {
      if (p.id === pageId) {
        return {...p, blocks: p.blocks.map(b => {
          if (b.id === blockId) {
            const items = [...(b.faq_items || [])];
            items[index] = {...items[index], [field]: value};
            return {...b, faq_items: items};
          }
          return b;
        })};
      }
      return p;
    }));
  };
  
  const removeFaqItem = (pageId, blockId, index) => {
    setPages(pages.map(p => {
      if (p.id === pageId) {
        return {...p, blocks: p.blocks.map(b => {
          if (b.id === blockId) {
            const items = [...(b.faq_items || [])];
            items.splice(index, 1);
            return {...b, faq_items: items};
          }
          return b;
        })};
      }
      return p;
    }));
  };
  
  // Gallery management
  const addGalleryImage = (pageId, blockId) => {
    setPages(pages.map(p => {
      if (p.id === pageId) {
        return {...p, blocks: p.blocks.map(b => {
          if (b.id === blockId) {
            const images = b.gallery_images || [];
            return {...b, gallery_images: [...images, '']};
          }
          return b;
        })};
      }
      return p;
    }));
  };
  
  const updateGalleryImage = (pageId, blockId, index, value) => {
    setPages(pages.map(p => {
      if (p.id === pageId) {
        return {...p, blocks: p.blocks.map(b => {
          if (b.id === blockId) {
            const images = [...(b.gallery_images || [])];
            images[index] = value;
            return {...b, gallery_images: images};
          }
          return b;
        })};
      }
      return p;
    }));
  };
  
  const removeGalleryImage = (pageId, blockId, index) => {
    setPages(pages.map(p => {
      if (p.id === pageId) {
        return {...p, blocks: p.blocks.map(b => {
          if (b.id === blockId) {
            const images = [...(b.gallery_images || [])];
            images.splice(index, 1);
            return {...b, gallery_images: images};
          }
          return b;
        })};
      }
      return p;
    }));
  };
  const updateBlock = (pageId, blockId, field, value) => {
    setPages(pages.map(p => {
      if (p.id === pageId) {
        return {...p, blocks: p.blocks.map(b => b.id === blockId ? {...b, [field]: value} : b)};
      }
      return p;
    }));
  };
  const deleteBlock = (pageId, blockId) => {
    setPages(pages.map(p => {
      if (p.id === pageId) {
        return {...p, blocks: p.blocks.filter(b => b.id !== blockId)};
      }
      return p;
    }));
  };
  const moveBlock = (pageId, blockId, direction) => {
    setPages(pages.map(p => {
      if (p.id === pageId) {
        const blocks = [...p.blocks];
        const idx = blocks.findIndex(b => b.id === blockId);
        if (direction === 'up' && idx > 0) {
          [blocks[idx], blocks[idx-1]] = [blocks[idx-1], blocks[idx]];
        } else if (direction === 'down' && idx < blocks.length - 1) {
          [blocks[idx], blocks[idx+1]] = [blocks[idx+1], blocks[idx]];
        }
        // Update order
        blocks.forEach((b, i) => b.order = i);
        return {...p, blocks};
      }
      return p;
    }));
  };

  // Drag & Drop state
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [dragOverBlock, setDragOverBlock] = useState(null);

  const handleDragStart = (e, pageId, blockId) => {
    setDraggedBlock({ pageId, blockId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', blockId);
    // Add drag image styling
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedBlock(null);
    setDragOverBlock(null);
  };

  const handleDragOver = (e, pageId, blockId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedBlock && draggedBlock.pageId === pageId && draggedBlock.blockId !== blockId) {
      setDragOverBlock({ pageId, blockId });
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if leaving the block entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverBlock(null);
    }
  };

  const handleDrop = (e, pageId, targetBlockId) => {
    e.preventDefault();
    if (!draggedBlock || draggedBlock.pageId !== pageId) return;
    
    setPages(pages.map(p => {
      if (p.id === pageId) {
        const blocks = [...p.blocks];
        const dragIdx = blocks.findIndex(b => b.id === draggedBlock.blockId);
        const dropIdx = blocks.findIndex(b => b.id === targetBlockId);
        
        if (dragIdx !== -1 && dropIdx !== -1 && dragIdx !== dropIdx) {
          // Remove dragged block and insert at new position
          const [removed] = blocks.splice(dragIdx, 1);
          blocks.splice(dropIdx, 0, removed);
          // Update order
          blocks.forEach((b, i) => b.order = i);
        }
        return {...p, blocks};
      }
      return p;
    }));
    
    setDraggedBlock(null);
    setDragOverBlock(null);
  };
  const savePage = async (page) => {
    try {
      // Generate slug if empty
      if (!page.slug) {
        page.slug = page.title.toLowerCase().replace(/\s+/g, '-').replace(/[äöüß]/g, c => ({ä:'ae',ö:'oe',ü:'ue',ß:'ss'})[c] || c);
      }
      if (page.id.startsWith('new-')) {
        const res = await axios.post(`${API}/admin/pages`, page, authHeaders());
        setPages(pages.map(p => p.id === page.id ? res.data : p));
        setPagesList([...pagesList, { id: res.data.id, title: res.data.title, slug: res.data.slug }]);
      } else {
        await axios.put(`${API}/admin/pages/${page.id}`, page, authHeaders());
        setPagesList(pagesList.map(p => p.id === page.id ? { id: page.id, title: page.title, slug: page.slug } : p));
      }
      toast.success('Seite gespeichert!');
    } catch (err) { 
      console.error(err);
      toast.error('Fehler'); 
    }
  };
  const deletePage = async (id) => {
    try {
      if (!id.startsWith('new-')) await axios.delete(`${API}/admin/pages/${id}`, authHeaders());
      setPages(pages.filter(p => p.id !== id));
      setPagesList(pagesList.filter(p => p.id !== id));
      toast.success('Gelöscht!');
    } catch { toast.error('Fehler'); }
  };

  // Social functions
  const addSocialPost = () => setSocial([...social, { id: `new-${Date.now()}`, profile: '', followers: '', category: '', image: '', logo: '', is_video: true, youtube_url: '', verified: true }]);
  const updateSocialPost = (id, field, value) => setSocial(social.map(s => s.id === id ? {...s, [field]: value} : s));
  const saveSocialPost = async (post) => {
    try {
      if (post.id.startsWith('new-')) {
        const res = await axios.post(`${API}/admin/social`, post, authHeaders());
        setSocial(social.map(s => s.id === post.id ? res.data : s));
      } else {
        await axios.put(`${API}/admin/social/${post.id}`, post, authHeaders());
      }
      toast.success('Gespeichert!');
    } catch { toast.error('Fehler'); }
  };
  const deleteSocialPost = async (id) => {
    try {
      if (!id.startsWith('new-')) await axios.delete(`${API}/admin/social/${id}`, authHeaders());
      setSocial(social.filter(s => s.id !== id));
      toast.success('Gelöscht!');
    } catch { toast.error('Fehler'); }
  };

  // Reference functions
  const addReference = () => setReferences([...references, { id: `new-${Date.now()}`, profile: '', followers: '', category: 'CGI Videos', image: '', logo: '', is_video: true, youtube_url: '', verified: true, featured: false }]);
  const updateReference = (id, field, value) => setReferences(references.map(r => r.id === id ? {...r, [field]: value} : r));
  const saveReference = async (ref) => {
    try {
      if (ref.id.startsWith('new-')) {
        const res = await axios.post(`${API}/admin/references`, ref, authHeaders());
        // Update with the new ID returned from server
        setReferences(references.map(r => r.id === ref.id ? res.data : r));
      } else {
        await axios.put(`${API}/admin/references/${ref.id}`, ref, authHeaders());
      }
      toast.success('Gespeichert!');
    } catch (err) { 
      console.error('Save reference error:', err);
      toast.error('Fehler'); 
    }
  };
  const deleteReference = async (id) => {
    try {
      const res = await axios.delete(`${API}/admin/references/${id}`, authHeaders());
      console.log('Delete response:', res.data);
      setReferences(references.filter(r => r.id !== id));
      toast.success('Gelöscht!');
    } catch (err) { 
      console.error('Delete reference error:', err);
      toast.error('Fehler beim Löschen'); 
    }
  };

  // Service functions
  const addService = () => setServices([...services, { id: `new-${Date.now()}`, title: '', description: '' }]);
  const updateService = (id, field, value) => setServices(services.map(s => s.id === id ? {...s, [field]: value} : s));
  const saveService = async (service) => {
    try {
      if (service.id.startsWith('new-')) {
        const res = await axios.post(`${API}/admin/services`, service, authHeaders());
        setServices(services.map(s => s.id === service.id ? res.data : s));
      } else {
        await axios.put(`${API}/admin/services/${service.id}`, service, authHeaders());
      }
      toast.success('Gespeichert!');
    } catch { toast.error('Fehler'); }
  };
  const deleteService = async (id) => {
    try {
      if (!id.startsWith('new-')) await axios.delete(`${API}/admin/services/${id}`, authHeaders());
      setServices(services.filter(s => s.id !== id));
      toast.success('Gelöscht!');
    } catch { toast.error('Fehler'); }
  };

  if (!content) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">Lädt...</div>;

  const tabs = [
    { id: 'content', label: 'Inhalte', icon: FileText },
    { id: 'logos', label: 'Logo Carousel', icon: Layout },
    { id: 'projects', label: 'Website Projekte', icon: Layout },
    { id: 'pages', label: 'Projekt-Seiten', icon: FileStack },
    { id: 'social', label: 'Social Media', icon: Video },
    { id: 'references', label: 'References Page', icon: BookOpen },
    { id: 'services', label: 'Services', icon: Globe },
    { id: 'products', label: 'Produkte', icon: Layers },
    { id: 'legal', label: 'Legal Pages', icon: FileEdit },
    { id: 'demo', label: 'Demo-Buchungen', icon: Calendar },
    { id: 'submissions', label: 'Anfragen', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Toaster position="top-center" richColors theme="dark" />
      
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">nyza-studio Admin</h1>
          <div className="flex items-center gap-4">
            {user && <span className="text-sm text-slate-400">{user.name || user.email}</span>}
            <a href="/" target="_blank" className="text-sm text-slate-400 hover:text-purple-400 flex items-center gap-1 transition-colors"><Eye className="w-4 h-4" /> Vorschau</a>
            <Button onClick={onLogout} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-slate-800 border-b border-slate-700 overflow-x-auto">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-3 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-1 border-b-2 transition-colors ${activeTab === tab.id ? 'border-purple-500 text-purple-400' : 'border-transparent text-slate-500 hover:text-white'}`}>
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        
        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="space-y-4">
            <div className="flex justify-end items-center gap-4">
              {saveStatus.message && (
                <span className={saveStatus.type === 'error' ? 'text-red-500 text-sm' : 'text-green-600 text-sm'}>
                  {saveStatus.message}
                </span>
              )}
              <Button onClick={saveContent} disabled={saving} className="btn-glow">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Speichert...' : 'Speichern'}
              </Button>
            </div>

            <SectionCard title="Hero Bereich" icon={FileText} defaultOpen={true}>
              <div className="grid gap-4 mt-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Markenname</label>
                  <Input value={content.brand_name || ''} onChange={(e) => setContent({...content, brand_name: e.target.value})} placeholder="z.B. nyza-studio" className="input-dark" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Hero Titel (große Überschrift)</label>
                  <Input value={content.hero_title || ''} onChange={(e) => setContent({...content, hero_title: e.target.value})} placeholder="z.B. WHERE PIXELS MEET MAGIC" className="input-dark" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Hero Subtitle</label>
                  <Input value={content.hero_subtitle || ''} onChange={(e) => setContent({...content, hero_subtitle: e.target.value})} placeholder="z.B. If you can dream it, we can create it!" className="input-dark" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Kontakt E-Mail</label>
                  <Input value={content.contact_email || ''} onChange={(e) => setContent({...content, contact_email: e.target.value})} placeholder="z.B. workwith@nyza-studio.at" className="input-dark" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Kontakt Telefonnummer (Contact Section)</label>
                  <Input value={content.contact_phone || ''} onChange={(e) => setContent({...content, contact_phone: e.target.value})} placeholder="z.B. +43 123 456789" className="input-dark" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">WhatsApp Nummer/Link (für Button)</label>
                  <Input value={content.contact_whatsapp || ''} onChange={(e) => setContent({...content, contact_whatsapp: e.target.value})} placeholder="z.B. 436641234567 oder https://wa.me/..." className="input-dark" />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Portfolio Texte" icon={Layout}>
              <div className="grid gap-4 mt-4">
                <Input value={content.portfolio_title || ''} onChange={(e) => setContent({...content, portfolio_title: e.target.value})} className="input-dark" placeholder="Portfolio Titel" />
                <Textarea value={content.portfolio_subtitle || ''} onChange={(e) => setContent({...content, portfolio_subtitle: e.target.value})} className="input-dark" placeholder="Portfolio Untertitel" />
                <Input value={content.portfolio_websites_title || ''} onChange={(e) => setContent({...content, portfolio_websites_title: e.target.value})} className="input-dark" placeholder="Website Projekte Titel" />
                <Input value={content.portfolio_social_title || ''} onChange={(e) => setContent({...content, portfolio_social_title: e.target.value})} className="input-dark" placeholder="Social Media Titel" />
              </div>
            </SectionCard>

            <SectionCard title="Services Texte" icon={Globe}>
              <div className="grid gap-4 mt-4">
                <Input value={content.services_title || ''} onChange={(e) => setContent({...content, services_title: e.target.value})} className="input-dark" placeholder="Services Titel" />
                <Textarea value={content.services_subtitle || ''} onChange={(e) => setContent({...content, services_subtitle: e.target.value})} className="bg-white border-2 border-gray-300 text-black focus:border-black min-h-[80px]" placeholder="Services Untertitel" />
              </div>
            </SectionCard>

            <SectionCard title="Kontakt & Footer" icon={Settings}>
              <div className="grid gap-4 mt-4">
                <Input value={content.contact_title || ''} onChange={(e) => setContent({...content, contact_title: e.target.value})} className="input-dark" placeholder="Kontakt Titel" />
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Kontakt Telefon (links im Formular)</label>
                  <Input value={content.contact_phone || ''} onChange={(e) => setContent({...content, contact_phone: e.target.value})} className="input-dark" placeholder="z.B. +43 123 456789" />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">WhatsApp (für Button unten rechts)</label>
                  <Input value={content.contact_whatsapp || ''} onChange={(e) => setContent({...content, contact_whatsapp: e.target.value})} className="input-dark" placeholder="WhatsApp Link oder Nummer" />
                </div>
                <Input value={content.footer_instagram || ''} onChange={(e) => setContent({...content, footer_instagram: e.target.value})} className="input-dark" placeholder="Instagram URL" />
                <Input value={content.footer_youtube || ''} onChange={(e) => setContent({...content, footer_youtube: e.target.value})} className="input-dark" placeholder="YouTube URL" />
                <Input value={content.footer_linkedin || ''} onChange={(e) => setContent({...content, footer_linkedin: e.target.value})} className="input-dark" placeholder="LinkedIn URL" />
                <hr className="border-gray-300 my-2" />
                <p className="text-sm font-medium text-black">Legal Links (Footer)</p>
                <Input value={content.footer_impressum_url || ''} onChange={(e) => setContent({...content, footer_impressum_url: e.target.value})} className="input-dark" placeholder="Impressum URL" />
                <Input value={content.footer_datenschutz_url || ''} onChange={(e) => setContent({...content, footer_datenschutz_url: e.target.value})} className="input-dark" placeholder="Datenschutz URL" />
                <Input value={content.footer_agb_url || ''} onChange={(e) => setContent({...content, footer_agb_url: e.target.value})} className="input-dark" placeholder="AGB URL" />
              </div>
            </SectionCard>
          </div>
        )}

        {/* Logos Tab */}
        {activeTab === 'logos' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <h2 className="text-xl font-semibold text-white">Logo Carousel (unter Hero)</h2>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple 
                    onChange={handleBulkLogoUpload} 
                    className="hidden" 
                  />
                  <div className="btn-glow px-4 py-2 rounded-md flex items-center text-sm font-medium">
                    {bulkUploading ? 'Lädt...' : <><Upload className="w-4 h-4 mr-2" /> Bulk Upload</>}
                  </div>
                </label>
                <Button onClick={addLogo} className="btn-glow"><Plus className="w-4 h-4 mr-2" /> Einzeln hinzufügen</Button>
              </div>
            </div>
            <p className="text-slate-400 text-sm">Logos scrollen langsam unter der Hero-Box. Mit Bulk Upload mehrere Logos gleichzeitig hochladen.</p>
            {logos.map((logo) => (
              <div key={logo.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input value={logo.name} onChange={(e) => updateLogo(logo.id, 'name', e.target.value)} className="input-dark" placeholder="Logo Name" />
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-slate-400 text-sm cursor-pointer">
                      <input type="checkbox" checked={logo.make_black !== false} onChange={(e) => updateLogo(logo.id, 'make_black', e.target.checked)} className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500" />
                      Automatisch weiß färben
                    </label>
                  </div>
                </div>
                <div className="mt-4"><ImageUpload label="Logo Bild" value={logo.url} onChange={(v) => updateLogo(logo.id, 'url', v)} /></div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => saveLogo(logo)} size="sm" className="btn-glow"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
                  <Button onClick={() => deleteLogo(logo.id)} size="sm" variant="destructive"><Trash2 className="w-4 h-4 mr-2" /> Löschen</Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Website Projekte</h2>
              <Button onClick={addProject} className="btn-glow"><Plus className="w-4 h-4 mr-2" /> Hinzufügen</Button>
            </div>
            <p className="text-slate-400 text-sm">Verknüpfe Projekte mit Detail-Seiten für mehr Infos.</p>
            {projects.map((project) => (
              <div key={project.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input value={project.title} onChange={(e) => updateProject(project.id, 'title', e.target.value)} className="input-dark" placeholder="Titel" />
                  <Input value={project.category} onChange={(e) => updateProject(project.id, 'category', e.target.value)} className="input-dark" placeholder="Kategorie" />
                </div>
                <div className="mt-4"><ImageUpload label="Screenshot (lang)" value={project.image} onChange={(v) => updateProject(project.id, 'image', v)} /></div>
                <div className="mt-4 flex items-center gap-4">
                  <label className="flex items-center gap-2 text-slate-400 text-sm cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={project.show_on_landing !== false} 
                      onChange={(e) => updateProject(project.id, 'show_on_landing', e.target.checked)} 
                      className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500" 
                    />
                    Auf Landing anzeigen (max. 4)
                  </label>
                </div>
                <div className="mt-4">
                  <label className="text-sm text-slate-400 block mb-2">Verknüpfte Detail-Seite</label>
                  <select 
                    value={project.linked_page_id || ''} 
                    onChange={(e) => updateProject(project.id, 'linked_page_id', e.target.value || null)}
                    className="input-dark w-full px-4 py-2"
                  >
                    <option value="">Keine Seite verknüpft</option>
                    {pagesList.map(p => (
                      <option key={p.id} value={p.id}>{p.title}</option>
                    ))}
                  </select>
                  {project.linked_page_id && pagesList.find(p => p.id === project.linked_page_id) && (
                    <a 
                      href={`/p/${pagesList.find(p => p.id === project.linked_page_id)?.slug}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-purple-400 text-sm mt-2 inline-flex items-center gap-1 hover:text-purple-300"
                    >
                      <ExternalLink className="w-3 h-3" /> Seite anzeigen
                    </a>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => saveProject(project)} size="sm" className="btn-glow"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
                  <Button onClick={() => deleteProject(project.id)} size="sm" variant="destructive"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pages Tab - Block Editor */}
        {activeTab === 'pages' && (
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 lg:h-[calc(100vh-200px)] lg:min-h-[600px]">
            {/* Sidebar - Pages List */}
            <div className="w-full lg:w-64 flex-shrink-0 bg-slate-800/50 border border-slate-700 rounded-xl p-4 lg:overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-white font-medium">Seiten</h3>
                <Button onClick={addPage} size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {pages.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">Noch keine Seiten</p>
              ) : (
                <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
                  {pages.map((page) => (
                    <button
                      key={page.id}
                      onClick={() => setSelectedPageId(page.id)}
                      className={`flex-shrink-0 lg:w-full text-left px-3 py-2 rounded-lg transition-all ${
                        selectedPageId === page.id 
                          ? 'bg-purple-600/30 border border-purple-500/50 text-white' 
                          : 'hover:bg-slate-700/50 text-slate-400 hover:text-white border border-transparent'
                      }`}
                    >
                      <div className="font-medium text-sm truncate whitespace-nowrap lg:whitespace-normal">{page.title || 'Unbenannt'}</div>
                      <div className="text-xs text-slate-500 truncate hidden lg:block">/p/{page.slug || '...'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Main Editor Area */}
            <div className="flex-1 lg:overflow-y-auto">
              {!selectedPageId || !pages.find(p => p.id === selectedPageId) ? (
                <div className="h-48 lg:h-full flex items-center justify-center">
                  <div className="text-center text-slate-500">
                    <FileStack className="w-12 lg:w-16 h-12 lg:h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-base lg:text-lg">Wähle eine Seite aus</p>
                    <p className="text-sm mt-1">oder erstelle eine neue Seite</p>
                  </div>
                </div>
              ) : (
                (() => {
                  const page = pages.find(p => p.id === selectedPageId);
                  if (!page) return null;
                  
                  return (
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 lg:p-6">
                      {/* Mobile: Back button */}
                      <button 
                        onClick={() => setSelectedPageId(null)}
                        className="lg:hidden flex items-center gap-2 text-slate-400 hover:text-white mb-4 text-sm"
                      >
                        <ArrowUp className="w-4 h-4 rotate-[-90deg]" /> Zurück zur Liste
                      </button>
                      
                      {/* Page Header */}
                      <div className="flex justify-between items-start mb-6">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-slate-400 block mb-1">Seitentitel</label>
                            <Input value={page.title} onChange={(e) => updatePage(page.id, 'title', e.target.value)} className="input-dark" placeholder="Seitentitel" />
                          </div>
                          <div>
                            <label className="text-sm text-slate-400 block mb-1">URL-Pfad</label>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500 text-sm">/p/</span>
                              <Input 
                                value={page.slug} 
                                onChange={(e) => updatePage(page.id, 'slug', e.target.value.toLowerCase().replace(/\s+/g, '-'))} 
                                className="input-dark" 
                                placeholder="url-pfad" 
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* SEO Settings - Collapsible */}
                      <details className="mb-6 group">
                        <summary className="cursor-pointer text-slate-400 hover:text-white text-sm flex items-center gap-2 py-2 border-b border-slate-700">
                          <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                          SEO Einstellungen
                        </summary>
                        <div className="pt-4 space-y-4">
                          <div>
                            <label className="text-sm text-slate-400 block mb-1">SEO Titel (Browser-Tab)</label>
                            <Input 
                              value={page.seo_title || ''} 
                              onChange={(e) => updatePage(page.id, 'seo_title', e.target.value)} 
                              className="input-dark" 
                              placeholder="z.B. Projektname | NYZA Studio" 
                            />
                            <p className="text-slate-600 text-xs mt-1">Leer lassen für Standard-Titel</p>
                          </div>
                          <div>
                            <label className="text-sm text-slate-400 block mb-1">Meta-Beschreibung</label>
                            <Textarea 
                              value={page.seo_description || ''} 
                              onChange={(e) => updatePage(page.id, 'seo_description', e.target.value)} 
                              className="input-dark min-h-[80px]" 
                              placeholder="Kurze Beschreibung für Suchmaschinen (150-160 Zeichen optimal)" 
                            />
                            <p className="text-slate-600 text-xs mt-1">{(page.seo_description || '').length}/160 Zeichen</p>
                          </div>
                          <div>
                            <label className="text-sm text-slate-400 block mb-1">Social Media Bild (OG Image)</label>
                            <ImageUpload 
                              value={page.seo_image || ''} 
                              onChange={(v) => updatePage(page.id, 'seo_image', v)} 
                            />
                            <p className="text-slate-600 text-xs mt-1">Optimal: 1200x630px</p>
                          </div>
                        </div>
                      </details>
                      
                      {/* Blocks Section */}
                      <div className="border-t border-slate-700 pt-4">
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h4 className="text-white font-medium">Inhaltsblöcke</h4>
                            <p className="text-slate-500 text-xs mt-1">Drag & Drop zum Sortieren</p>
                          </div>
                          <div className="relative">
                            <Button 
                              onClick={() => setShowBlockTypeMenu(showBlockTypeMenu === page.id ? null : page.id)} 
                              size="sm" 
                              variant="outline" 
                              className="border-slate-600 text-slate-300 hover:bg-slate-700"
                            >
                              <Plus className="w-4 h-4 mr-1" /> Block
                            </Button>
                            {showBlockTypeMenu === page.id && (
                              <div className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-20 overflow-hidden">
                                {BLOCK_TYPES.map((bt) => (
                                  <button
                                    key={bt.id}
                                    onClick={() => addBlockToPage(page.id, bt.id)}
                                    className="w-full px-4 py-3 flex items-start gap-3 hover:bg-slate-700 transition-colors text-left"
                                  >
                                    <bt.icon className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <div className="text-white text-sm font-medium">{bt.label}</div>
                                      <div className="text-slate-500 text-xs">{bt.description}</div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Blocks List */}
                        {page.blocks && page.blocks.length > 0 ? (
                          <div className="space-y-3">
                            {page.blocks.sort((a,b) => a.order - b.order).map((block, idx) => {
                              const isDragging = draggedBlock?.pageId === page.id && draggedBlock?.blockId === block.id;
                              const isDragOver = dragOverBlock?.pageId === page.id && dragOverBlock?.blockId === block.id;
                              const blockType = BLOCK_TYPES.find(bt => bt.id === (block.type || 'image_text')) || BLOCK_TYPES[0];
                              
                              return (
                                <div 
                                  key={block.id} 
                                  className={`bg-slate-900 border rounded-lg p-4 transition-all duration-200 ${
                                    isDragging ? 'opacity-50 border-purple-500' : 
                                    isDragOver ? 'border-purple-400 border-2 bg-purple-900/20' : 
                                    'border-slate-700'
                                  }`}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, page.id, block.id)}
                                  onDragEnd={handleDragEnd}
                                  onDragOver={(e) => handleDragOver(e, page.id, block.id)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, page.id, block.id)}
                                >
                                  <div className="flex justify-between items-center mb-3">
                                    <span className="text-slate-400 text-sm flex items-center gap-2 cursor-grab active:cursor-grabbing">
                                      <GripVertical className="w-4 h-4 text-purple-400" />
                                      <blockType.icon className="w-4 h-4 text-slate-500" />
                                      {blockType.label}
                                    </span>
                                    <div className="flex gap-1">
                                      <Button onClick={() => moveBlock(page.id, block.id, 'up')} size="sm" variant="ghost" className="p-1 h-8 text-slate-400 hover:text-white" disabled={idx === 0}>
                                        <ArrowUp className="w-4 h-4" />
                                      </Button>
                                      <Button onClick={() => moveBlock(page.id, block.id, 'down')} size="sm" variant="ghost" className="p-1 h-8 text-slate-400 hover:text-white" disabled={idx === page.blocks.length - 1}>
                                        <ArrowDown className="w-4 h-4" />
                                      </Button>
                                      <Button onClick={() => deleteBlock(page.id, block.id)} size="sm" variant="ghost" className="p-1 h-8 text-red-400 hover:text-red-300">
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  {/* Image + Text Block */}
                                  {(block.type === 'image_text' || !block.type) && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <ImageUpload label="Bild" value={block.image} onChange={(v) => updateBlock(page.id, block.id, 'image', v)} />
                                      <div className="space-y-3">
                                        <div>
                                          <label className="text-sm text-slate-400 block mb-1">Überschrift</label>
                                          <Input value={block.title || ''} onChange={(e) => updateBlock(page.id, block.id, 'title', e.target.value)} className="input-dark" placeholder="Block Überschrift" />
                                        </div>
                                        <div>
                                          <label className="text-sm text-slate-400 block mb-1">Text</label>
                                          <Textarea value={block.text || ''} onChange={(e) => updateBlock(page.id, block.id, 'text', e.target.value)} className="input-dark min-h-[100px]" placeholder="Beschreibungstext..." />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Single Image Block */}
                                  {block.type === 'single_image' && (
                                    <div className="space-y-4">
                                      <ImageUpload label="Bild" value={block.image} onChange={(v) => updateBlock(page.id, block.id, 'image', v)} />
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                          <label className="text-sm text-slate-400 block mb-1">Titel (optional)</label>
                                          <Input value={block.title || ''} onChange={(e) => updateBlock(page.id, block.id, 'title', e.target.value)} className="input-dark" placeholder="Bild Titel" />
                                        </div>
                                        <div>
                                          <label className="text-sm text-slate-400 block mb-1">Beschreibung (optional)</label>
                                          <Input value={block.text || ''} onChange={(e) => updateBlock(page.id, block.id, 'text', e.target.value)} className="input-dark" placeholder="Kurze Beschreibung" />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Video Block */}
                                  {block.type === 'video' && (
                                    <div className="space-y-4">
                                      <div>
                                        <label className="text-sm text-slate-400 block mb-1">YouTube URL</label>
                                        <Input value={block.video_url || ''} onChange={(e) => updateBlock(page.id, block.id, 'video_url', e.target.value)} className="input-dark" placeholder="https://youtube.com/watch?v=..." />
                                      </div>
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <label className="text-sm text-slate-400 block mb-1">Titel</label>
                                          <Input value={block.title || ''} onChange={(e) => updateBlock(page.id, block.id, 'title', e.target.value)} className="input-dark" placeholder="Video Titel" />
                                        </div>
                                        <div>
                                          <label className="text-sm text-slate-400 block mb-1">Beschreibung</label>
                                          <Input value={block.text || ''} onChange={(e) => updateBlock(page.id, block.id, 'text', e.target.value)} className="input-dark" placeholder="Kurze Beschreibung" />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Gallery Block */}
                                  {block.type === 'gallery' && (
                                    <div className="space-y-4">
                                      <div>
                                        <label className="text-sm text-slate-400 block mb-1">Galerie Titel</label>
                                        <Input value={block.title || ''} onChange={(e) => updateBlock(page.id, block.id, 'title', e.target.value)} className="input-dark" placeholder="Galerie Überschrift" />
                                      </div>
                                      <div>
                                        <div className="flex justify-between items-center mb-2">
                                          <label className="text-sm text-slate-400">Bilder ({(block.gallery_images || []).length})</label>
                                          <Button onClick={() => addGalleryImage(page.id, block.id)} size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 h-7 text-xs">
                                            <Plus className="w-3 h-3 mr-1" /> Bild
                                          </Button>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                          {(block.gallery_images || []).map((img, imgIdx) => (
                                            <div key={imgIdx} className="relative aspect-square">
                                              <ImageUpload value={img} onChange={(v) => updateGalleryImage(page.id, block.id, imgIdx, v)} />
                                              <button 
                                                onClick={() => removeGalleryImage(page.id, block.id, imgIdx)}
                                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-400 z-10"
                                              >
                                                <X className="w-3 h-3" />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* FAQ Block */}
                                  {block.type === 'faq' && (
                                    <div className="space-y-4">
                                      <div>
                                        <label className="text-sm text-slate-400 block mb-1">FAQ Titel</label>
                                        <Input value={block.title || ''} onChange={(e) => updateBlock(page.id, block.id, 'title', e.target.value)} className="input-dark" placeholder="Häufig gestellte Fragen" />
                                      </div>
                                      <div>
                                        <div className="flex justify-between items-center mb-2">
                                          <label className="text-sm text-slate-400">Fragen ({(block.faq_items || []).length})</label>
                                          <Button onClick={() => addFaqItem(page.id, block.id)} size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 h-7 text-xs">
                                            <Plus className="w-3 h-3 mr-1" /> Frage
                                          </Button>
                                        </div>
                                        <div className="space-y-2">
                                          {(block.faq_items || []).map((faq, faqIdx) => (
                                            <div key={faqIdx} className="bg-slate-800 rounded-lg p-3 relative">
                                              <button 
                                                onClick={() => removeFaqItem(page.id, block.id, faqIdx)}
                                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-400"
                                              >
                                                <X className="w-3 h-3" />
                                              </button>
                                              <Input 
                                                value={faq.question || ''} 
                                                onChange={(e) => updateFaqItem(page.id, block.id, faqIdx, 'question', e.target.value)} 
                                                className="input-dark mb-2" 
                                                placeholder="Frage..." 
                                              />
                                              <Textarea 
                                                value={faq.answer || ''} 
                                                onChange={(e) => updateFaqItem(page.id, block.id, faqIdx, 'answer', e.target.value)} 
                                                className="input-dark min-h-[50px]" 
                                                placeholder="Antwort..." 
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* CTA Block */}
                                  {block.type === 'cta' && (
                                    <div className="space-y-4">
                                      <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-lg p-4 border border-purple-500/30">
                                        <div className="text-center py-2">
                                          <p className="text-white font-bold">{block.title || 'CTA Titel'}</p>
                                          <p className="text-slate-300 text-sm">{block.text || 'Beschreibung'}</p>
                                          <span className="inline-block mt-2 bg-purple-600 text-white px-3 py-1 rounded-lg text-sm">{block.cta_button_text || 'Button'}</span>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                          <label className="text-sm text-slate-400 block mb-1">Titel</label>
                                          <Input value={block.title || ''} onChange={(e) => updateBlock(page.id, block.id, 'title', e.target.value)} className="input-dark" placeholder="z.B. Interesse geweckt?" />
                                        </div>
                                        <div>
                                          <label className="text-sm text-slate-400 block mb-1">Button Text</label>
                                          <Input value={block.cta_button_text || ''} onChange={(e) => updateBlock(page.id, block.id, 'cta_button_text', e.target.value)} className="input-dark" placeholder="z.B. Kontakt" />
                                        </div>
                                        <div>
                                          <label className="text-sm text-slate-400 block mb-1">Beschreibung</label>
                                          <Input value={block.text || ''} onChange={(e) => updateBlock(page.id, block.id, 'text', e.target.value)} className="input-dark" placeholder="Kurzer Text" />
                                        </div>
                                        <div>
                                          <label className="text-sm text-slate-400 block mb-1">Button Link</label>
                                          <Input value={block.cta_button_link || ''} onChange={(e) => updateBlock(page.id, block.id, 'cta_button_link', e.target.value)} className="input-dark" placeholder="/#contact" />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-slate-500 border border-dashed border-slate-700 rounded-lg">
                            <p className="text-sm">Noch keine Blöcke</p>
                            <p className="text-xs mt-1">Klicke auf "+ Block" um Inhalte hinzuzufügen</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Page Actions */}
                      <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-slate-700">
                        <Button onClick={() => savePage(page)} className="btn-glow">
                          <Save className="w-4 h-4 mr-2" /> Speichern
                        </Button>
                        {page.slug && !page.id.startsWith('new-') && (
                          <a 
                            href={`/p/${page.slug}`} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-purple-400 hover:text-purple-300 border border-purple-500/30 rounded-lg hover:bg-purple-500/10 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" /> Vorschau
                          </a>
                        )}
                        <Button onClick={() => deletePage(page.id)} variant="destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        )}

        {/* Social Tab */}
        {activeTab === 'social' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Social Media (Startseite)</h2>
              <Button onClick={addSocialPost} className="btn-glow"><Plus className="w-4 h-4 mr-2" /> Hinzufügen</Button>
            </div>
            <p className="text-slate-400 text-sm">9:16 Videos für die Startseite mit YouTube Shorts Links</p>
            {social.map((post) => (
              <div key={post.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <div className="grid grid-cols-3 gap-4">
                  <Input value={post.profile} onChange={(e) => updateSocialPost(post.id, 'profile', e.target.value)} className="input-dark" placeholder="@username" />
                  <Input value={post.followers} onChange={(e) => updateSocialPost(post.id, 'followers', e.target.value)} className="input-dark" placeholder="Follower" />
                  <Input value={post.category} onChange={(e) => updateSocialPost(post.id, 'category', e.target.value)} className="input-dark" placeholder="Kategorie" />
                </div>
                <div className="mt-4">
                  <Input value={post.youtube_url || ''} onChange={(e) => updateSocialPost(post.id, 'youtube_url', e.target.value)} className="input-dark" placeholder="YouTube Short URL" />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <ImageUpload label="Vorschaubild (9:16)" value={post.image} onChange={(v) => updateSocialPost(post.id, 'image', v)} />
                  <ImageUpload label="Profil Logo" value={post.logo} onChange={(v) => updateSocialPost(post.id, 'logo', v)} />
                </div>
                <div className="flex gap-4 mt-4">
                  <label className="flex items-center gap-2 text-gray-600 text-sm">
                    <input type="checkbox" checked={post.is_video} onChange={(e) => updateSocialPost(post.id, 'is_video', e.target.checked)} />Video
                  </label>
                  <label className="flex items-center gap-2 text-gray-600 text-sm">
                    <input type="checkbox" checked={post.verified} onChange={(e) => updateSocialPost(post.id, 'verified', e.target.checked)} />Verifiziert
                  </label>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => saveSocialPost(post)} size="sm" className="btn-glow"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
                  <Button onClick={() => deleteSocialPost(post.id)} size="sm" variant="destructive"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* References Tab */}
        {activeTab === 'references' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">References Page</h2>
              <Button onClick={addReference} className="btn-glow"><Plus className="w-4 h-4 mr-2" /> Hinzufügen</Button>
            </div>
            <p className="text-slate-400 text-sm">Sortiert nach Kategorie. ⭐ = Wird auf der Startseite angezeigt (2 pro Kategorie)</p>
            {references.map((ref) => (
              <div key={ref.id} className={`bg-slate-800 border rounded-xl p-4 ${ref.featured ? 'border-yellow-500' : 'border-slate-700'}`}>
                <div className="grid grid-cols-3 gap-4">
                  <Input value={ref.profile} onChange={(e) => updateReference(ref.id, 'profile', e.target.value)} className="input-dark" placeholder="@username" />
                  <Input value={ref.followers} onChange={(e) => updateReference(ref.id, 'followers', e.target.value)} className="input-dark" placeholder="Follower" />
                  <select value={ref.category} onChange={(e) => updateReference(ref.id, 'category', e.target.value)} className="bg-slate-700 border-2 border-slate-600 text-white rounded-lg px-3 py-2">
                    {REFERENCE_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div className="mt-4">
                  <Input value={ref.youtube_url || ''} onChange={(e) => updateReference(ref.id, 'youtube_url', e.target.value)} className="input-dark" placeholder="YouTube Short URL" />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <ImageUpload label="Vorschaubild (9:16)" value={ref.image} onChange={(v) => updateReference(ref.id, 'image', v)} />
                  <ImageUpload label="Profil Logo" value={ref.logo} onChange={(v) => updateReference(ref.id, 'logo', v)} />
                </div>
                <div className="flex gap-4 mt-4">
                  <label className="flex items-center gap-2 text-yellow-400 text-sm font-medium cursor-pointer">
                    <input type="checkbox" checked={ref.featured || false} onChange={(e) => updateReference(ref.id, 'featured', e.target.checked)} className="w-4 h-4 accent-yellow-400" />
                    ⭐ Startseite
                  </label>
                  <label className="flex items-center gap-2 text-gray-600 text-sm">
                    <input type="checkbox" checked={ref.is_video} onChange={(e) => updateReference(ref.id, 'is_video', e.target.checked)} />Video
                  </label>
                  <label className="flex items-center gap-2 text-gray-600 text-sm">
                    <input type="checkbox" checked={ref.verified} onChange={(e) => updateReference(ref.id, 'verified', e.target.checked)} />Verifiziert
                  </label>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => saveReference(ref)} size="sm" className="btn-glow"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
                  <Button onClick={() => deleteReference(ref.id)} size="sm" variant="destructive"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Services</h2>
              <Button onClick={addService} className="btn-glow"><Plus className="w-4 h-4 mr-2" /> Hinzufügen</Button>
            </div>
            {services.map((service) => (
              <div key={service.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <Input value={service.title} onChange={(e) => updateService(service.id, 'title', e.target.value)} className="bg-white border-2 border-gray-300 text-black focus:border-black mb-4" placeholder="Titel" />
                <Textarea value={service.description} onChange={(e) => updateService(service.id, 'description', e.target.value)} className="bg-white border-2 border-gray-300 text-black focus:border-black min-h-[80px]" placeholder="Beschreibung" />
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => saveService(service)} size="sm" className="btn-glow"><Save className="w-4 h-4 mr-2" /> Speichern</Button>
                  <Button onClick={() => deleteService(service.id)} size="sm" variant="destructive"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            
            {/* Products List */}
            <SectionCard title="Produkte / Lösungen" icon={Package}>
              <div className="space-y-4">
                {products.map((product) => (
                  <div key={product.id} className={`bg-slate-700/50 rounded-lg p-4 border-l-4 ${product.is_popular ? 'border-yellow-500' : 'border-slate-600'} ${!product.active ? 'opacity-50' : ''}`}>
                    <div className="flex items-start gap-4">
                      {/* Product Image */}
                      <div className="w-24 h-24 flex-shrink-0">
                        {product.image ? (
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <div className="w-full h-full bg-slate-600 rounded-lg flex items-center justify-center">
                            <Package className="w-8 h-8 text-slate-400" />
                          </div>
                        )}
                      </div>
                      
                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-semibold text-lg">{product.name}</h3>
                          {product.is_popular && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                          {!product.active && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Inaktiv</span>}
                        </div>
                        <p className="text-purple-400 text-sm font-medium mb-1">{product.tagline || 'Kein Tagline'}</p>
                        <p className="text-slate-400 text-sm line-clamp-2">{product.short_description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> /{product.slug}</span>
                          <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> {product.price || '—'}€</span>
                          <span>{product.features?.length || 0} Features</span>
                          <span>{product.benefits?.length || 0} Benefits</span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm"
                          onClick={() => setEditingProduct(editingProduct?.id === product.id ? null : {...product})}
                          className="bg-slate-600 hover:bg-slate-500"
                        >
                          {editingProduct?.id === product.id ? <X className="w-3 h-3" /> : <FileEdit className="w-3 h-3" />}
                        </Button>
                        <Button 
                          size="sm"
                          variant="destructive"
                          onClick={async () => {
                            if (confirm('Produkt wirklich löschen?')) {
                              try {
                                await axios.delete(`${API}/admin/products/${product.id}`, authHeaders());
                                setProducts(products.filter(p => p.id !== product.id));
                                toast.success('Produkt gelöscht');
                              } catch (err) {
                                toast.error('Fehler beim Löschen');
                              }
                            }
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Edit Form */}
                    {editingProduct?.id === product.id && (
                      <div className="mt-6 pt-6 border-t border-slate-600 space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-slate-300 text-sm block mb-1">Name *</label>
                            <Input 
                              value={editingProduct.name}
                              onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                              className="bg-slate-700 border-slate-600 text-white"
                            />
                          </div>
                          <div>
                            <label className="text-slate-300 text-sm block mb-1">URL Slug *</label>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">/products/</span>
                              <Input 
                                value={editingProduct.slug}
                                onChange={(e) => setEditingProduct({...editingProduct, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                                className="bg-slate-700 border-slate-600 text-white flex-1"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-slate-300 text-sm block mb-1">Tagline</label>
                            <Input 
                              value={editingProduct.tagline}
                              onChange={(e) => setEditingProduct({...editingProduct, tagline: e.target.value})}
                              placeholder="z.B. 'Automotive Control.'"
                              className="bg-slate-700 border-slate-600 text-white"
                            />
                          </div>
                          <div>
                            <label className="text-slate-300 text-sm block mb-1">Kategorie</label>
                            <Input 
                              value={editingProduct.category}
                              onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
                              placeholder="z.B. 'Automotive', 'Management'"
                              className="bg-slate-700 border-slate-600 text-white"
                            />
                          </div>
                          <div>
                            <label className="text-slate-300 text-sm block mb-1">Icon</label>
                            <select 
                              value={editingProduct.icon}
                              onChange={(e) => setEditingProduct({...editingProduct, icon: e.target.value})}
                              className="w-full bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2"
                            >
                              <option value="layers">Layers</option>
                              <option value="car">Car</option>
                              <option value="users">Users</option>
                              <option value="building">Building</option>
                              <option value="utensils">Utensils</option>
                              <option value="home">Home</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-slate-300 text-sm block mb-1">Preis (€/Monat)</label>
                            <Input 
                              value={editingProduct.price}
                              onChange={(e) => setEditingProduct({...editingProduct, price: e.target.value})}
                              placeholder="z.B. '99'"
                              className="bg-slate-700 border-slate-600 text-white"
                            />
                          </div>
                        </div>
                        
                        {/* Short Description */}
                        <div>
                          <label className="text-slate-300 text-sm block mb-1">Kurzbeschreibung (für Übersicht)</label>
                          <Textarea 
                            value={editingProduct.short_description}
                            onChange={(e) => setEditingProduct({...editingProduct, short_description: e.target.value})}
                            placeholder="Wird auf der Products-Übersicht angezeigt..."
                            className="bg-slate-700 border-slate-600 text-white"
                            rows={2}
                          />
                        </div>
                        
                        {/* Full Description */}
                        <div>
                          <label className="text-slate-300 text-sm block mb-1">Vollständige Beschreibung (für Detailseite)</label>
                          <Textarea 
                            value={editingProduct.description}
                            onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                            placeholder="Ausführliche Beschreibung des Produkts..."
                            className="bg-slate-700 border-slate-600 text-white"
                            rows={4}
                          />
                        </div>
                        
                        {/* Image Upload */}
                        <div>
                          <label className="text-slate-300 text-sm block mb-1">Produktbild</label>
                          <ImageUpload 
                            value={editingProduct.image} 
                            onChange={(url) => setEditingProduct({...editingProduct, image: url})}
                            label="Produktbild hochladen"
                          />
                        </div>
                        
                        {/* Features */}
                        <div>
                          <label className="text-slate-300 text-sm block mb-2">Features</label>
                          <div className="space-y-3">
                            {(editingProduct.features || []).map((feature, idx) => (
                              <div key={idx} className="bg-slate-800 rounded-lg p-3 flex gap-3">
                                <div className="flex-1 space-y-2">
                                  <Input 
                                    value={feature.title}
                                    onChange={(e) => {
                                      const newFeatures = [...editingProduct.features];
                                      newFeatures[idx] = {...feature, title: e.target.value};
                                      setEditingProduct({...editingProduct, features: newFeatures});
                                    }}
                                    placeholder="Feature Titel"
                                    className="bg-slate-700 border-slate-600 text-white"
                                  />
                                  <Textarea 
                                    value={feature.description}
                                    onChange={(e) => {
                                      const newFeatures = [...editingProduct.features];
                                      newFeatures[idx] = {...feature, description: e.target.value};
                                      setEditingProduct({...editingProduct, features: newFeatures});
                                    }}
                                    placeholder="Feature Beschreibung"
                                    className="bg-slate-700 border-slate-600 text-white"
                                    rows={2}
                                  />
                                </div>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => {
                                    const newFeatures = editingProduct.features.filter((_, i) => i !== idx);
                                    setEditingProduct({...editingProduct, features: newFeatures});
                                  }}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                            <Button 
                              size="sm"
                              onClick={() => setEditingProduct({
                                ...editingProduct, 
                                features: [...(editingProduct.features || []), { title: '', description: '', icon: 'layers' }]
                              })}
                              className="bg-slate-600 hover:bg-slate-500"
                            >
                              <Plus className="w-3 h-3 mr-1" /> Feature hinzufügen
                            </Button>
                          </div>
                        </div>
                        
                        {/* Benefits */}
                        <div>
                          <label className="text-slate-300 text-sm block mb-2">Benefits (Vorteile)</label>
                          <div className="space-y-2">
                            {(editingProduct.benefits || []).map((benefit, idx) => (
                              <div key={idx} className="flex gap-2">
                                <Input 
                                  value={benefit}
                                  onChange={(e) => {
                                    const newBenefits = [...editingProduct.benefits];
                                    newBenefits[idx] = e.target.value;
                                    setEditingProduct({...editingProduct, benefits: newBenefits});
                                  }}
                                  placeholder="z.B. 'Unlimited Users'"
                                  className="bg-slate-700 border-slate-600 text-white flex-1"
                                />
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => {
                                    const newBenefits = editingProduct.benefits.filter((_, i) => i !== idx);
                                    setEditingProduct({...editingProduct, benefits: newBenefits});
                                  }}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                            <Button 
                              size="sm"
                              onClick={() => setEditingProduct({
                                ...editingProduct, 
                                benefits: [...(editingProduct.benefits || []), '']
                              })}
                              className="bg-slate-600 hover:bg-slate-500"
                            >
                              <Plus className="w-3 h-3 mr-1" /> Benefit hinzufügen
                            </Button>
                          </div>
                        </div>
                        
                        {/* Status Toggles */}
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox"
                              checked={editingProduct.is_popular}
                              onChange={(e) => setEditingProduct({...editingProduct, is_popular: e.target.checked})}
                              className="w-4 h-4 rounded text-purple-600"
                            />
                            <span className="text-slate-300 text-sm">⭐ Als "Most Popular" markieren</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox"
                              checked={editingProduct.active}
                              onChange={(e) => setEditingProduct({...editingProduct, active: e.target.checked})}
                              className="w-4 h-4 rounded text-purple-600"
                            />
                            <span className="text-slate-300 text-sm">Aktiv (sichtbar)</span>
                          </label>
                        </div>
                        
                        {/* Save Button */}
                        <div className="flex gap-3">
                          <Button 
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/admin/products/${editingProduct.id}`, editingProduct, authHeaders());
                                setProducts(products.map(p => p.id === editingProduct.id ? editingProduct : p));
                                setEditingProduct(null);
                                toast.success('Produkt gespeichert');
                              } catch (err) {
                                toast.error('Fehler beim Speichern');
                              }
                            }}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <Save className="w-4 h-4 mr-2" /> Speichern
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => setEditingProduct(null)}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                          >
                            Abbrechen
                          </Button>
                          <a 
                            href={`/products/${editingProduct.slug}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" /> Vorschau
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {products.length === 0 && (
                  <p className="text-slate-400 text-center py-8">Keine Produkte vorhanden</p>
                )}
              </div>
            </SectionCard>
            
            {/* Add New Product */}
            <SectionCard title="Neues Produkt erstellen" icon={Plus}>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-slate-300 text-sm block mb-1">Name *</label>
                    <Input 
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                      placeholder="z.B. 'CardealerOS'"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-slate-300 text-sm block mb-1">URL Slug *</label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">/products/</span>
                      <Input 
                        value={newProduct.slug}
                        onChange={(e) => setNewProduct({...newProduct, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                        placeholder="cardealeros"
                        className="bg-slate-700 border-slate-600 text-white flex-1"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-slate-300 text-sm block mb-1">Tagline</label>
                  <Input 
                    value={newProduct.tagline}
                    onChange={(e) => setNewProduct({...newProduct, tagline: e.target.value})}
                    placeholder="z.B. 'Automotive Control.'"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="text-slate-300 text-sm block mb-1">Kurzbeschreibung</label>
                  <Textarea 
                    value={newProduct.short_description}
                    onChange={(e) => setNewProduct({...newProduct, short_description: e.target.value})}
                    placeholder="Kurze Beschreibung für die Übersicht..."
                    className="bg-slate-700 border-slate-600 text-white"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-slate-300 text-sm block mb-1">Produktbild</label>
                  <ImageUpload 
                    value={newProduct.image} 
                    onChange={(url) => setNewProduct({...newProduct, image: url})}
                    label="Bild hochladen"
                  />
                </div>
                <Button 
                  onClick={async () => {
                    if (!newProduct.name || !newProduct.slug) {
                      toast.error('Name und Slug sind erforderlich');
                      return;
                    }
                    try {
                      const res = await axios.post(`${API}/admin/products`, {
                        ...newProduct,
                        order: products.length
                      }, authHeaders());
                      await fetchProducts();
                      setNewProduct({
                        name: '', slug: '', tagline: '', short_description: '', description: '',
                        category: '', icon: 'layers', image: '', features: [], benefits: [],
                        price: '', is_popular: false, active: true
                      });
                      toast.success('Produkt erstellt! Klicke auf Bearbeiten um Details hinzuzufügen.');
                    } catch (err) {
                      toast.error('Fehler beim Erstellen');
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" /> Produkt erstellen
                </Button>
              </div>
            </SectionCard>
          </div>
        )}

        {/* Legal Pages Tab */}
        {activeTab === 'legal' && (
          <div className="space-y-4">
            <div className="flex justify-end mb-4">
              <Button onClick={saveContent} disabled={saving} className="btn-glow">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Speichert...' : 'Speichern'}
              </Button>
            </div>
            
            <SectionCard title="Impressum" icon={FileEdit} defaultOpen={true}>
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">HTML-Format erlaubt</p>
                <Textarea 
                  value={content.impressum_content || ''} 
                  onChange={(e) => setContent({...content, impressum_content: e.target.value})} 
                  className="bg-white border-2 border-gray-300 text-black focus:border-black min-h-[200px] font-mono text-sm" 
                  placeholder="Impressum Inhalt (HTML)"
                />
              </div>
            </SectionCard>
            
            <SectionCard title="Datenschutzerklärung" icon={FileEdit}>
              <div className="mt-4">
                <Textarea 
                  value={content.datenschutz_content || ''} 
                  onChange={(e) => setContent({...content, datenschutz_content: e.target.value})} 
                  className="bg-white border-2 border-gray-300 text-black focus:border-black min-h-[300px] font-mono text-sm" 
                  placeholder="Datenschutz Inhalt (HTML)"
                />
              </div>
            </SectionCard>
            
            <SectionCard title="AGB" icon={FileEdit}>
              <div className="mt-4">
                <Textarea 
                  value={content.agb_content || ''} 
                  onChange={(e) => setContent({...content, agb_content: e.target.value})} 
                  className="bg-white border-2 border-gray-300 text-black focus:border-black min-h-[300px] font-mono text-sm" 
                  placeholder="AGB Inhalt (HTML)"
                />
              </div>
            </SectionCard>
          </div>
        )}

        {/* Demo Bookings Tab */}
        {activeTab === 'demo' && (
          <div className="space-y-6">
            
            {/* Demo Apps Management */}
            <SectionCard title="Demo Apps / Systeme" icon={Settings}>
              <div className="space-y-4">
                {demoApps.map((app) => (
                  <div key={app.id} className="bg-slate-700/50 rounded-lg p-4 flex items-start gap-4">
                    <img src={app.image || 'https://via.placeholder.com/60x60/7c3aed/ffffff?text=?'} alt={app.name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <input 
                        value={app.name} 
                        onChange={(e) => setDemoApps(demoApps.map(a => a.id === app.id ? {...a, name: e.target.value} : a))}
                        className="bg-transparent border-b border-slate-600 text-white font-semibold w-full focus:border-purple-500 outline-none pb-1"
                        placeholder="App Name"
                      />
                      <textarea 
                        value={app.description} 
                        onChange={(e) => setDemoApps(demoApps.map(a => a.id === app.id ? {...a, description: e.target.value} : a))}
                        className="bg-transparent border-b border-slate-600 text-slate-300 text-sm w-full focus:border-purple-500 outline-none mt-2 resize-none"
                        placeholder="Kurze Beschreibung"
                        rows={2}
                      />
                      <div className="mt-2">
                        <ImageUpload 
                          value={app.image} 
                          onChange={(url) => setDemoApps(demoApps.map(a => a.id === app.id ? {...a, image: url} : a))}
                          label="App Bild"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button 
                        size="sm"
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/demo/apps/${app.id}`, app, authHeaders());
                            toast.success('App gespeichert');
                          } catch (err) {
                            toast.error('Fehler beim Speichern');
                          }
                        }}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Save className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm"
                        variant="destructive"
                        onClick={async () => {
                          if (confirm('App wirklich löschen?')) {
                            try {
                              await axios.delete(`${API}/admin/demo/apps/${app.id}`, authHeaders());
                              setDemoApps(demoApps.filter(a => a.id !== app.id));
                              toast.success('App gelöscht');
                            } catch (err) {
                              toast.error('Fehler beim Löschen');
                            }
                          }
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Add new app */}
                <div className="border-2 border-dashed border-slate-600 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">Neue App hinzufügen</h4>
                  <div className="space-y-3">
                    <Input 
                      value={newDemoApp.name} 
                      onChange={(e) => setNewDemoApp({...newDemoApp, name: e.target.value})}
                      placeholder="App Name"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                    <ImageUpload 
                      value={newDemoApp.image} 
                      onChange={(url) => setNewDemoApp({...newDemoApp, image: url})}
                      label="App Bild"
                    />
                    <Input 
                      value={newDemoApp.description} 
                      onChange={(e) => setNewDemoApp({...newDemoApp, description: e.target.value})}
                      placeholder="Beschreibung"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <Button 
                    className="mt-3 bg-purple-600 hover:bg-purple-700"
                    onClick={async () => {
                      if (!newDemoApp.name) return toast.error('Name erforderlich');
                      try {
                        const res = await axios.post(`${API}/admin/demo/apps`, {
                          ...newDemoApp,
                          id: `app-${Date.now()}`,
                          order: demoApps.length
                        }, authHeaders());
                        setDemoApps([...demoApps, res.data.app]);
                        setNewDemoApp({ name: '', description: '', image: '', order: 0, active: true });
                        toast.success('App erstellt');
                      } catch (err) {
                        toast.error('Fehler beim Erstellen');
                      }
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" /> App hinzufügen
                  </Button>
                </div>
              </div>
            </SectionCard>

            {/* Demo Requests */}
            <SectionCard title="Demo-Anfragen" icon={Calendar}>
              <div className="space-y-3">
                {demoRequests.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">Keine Demo-Anfragen</p>
                ) : demoRequests.map((req) => (
                  <div key={req.id} className={`bg-slate-700/50 rounded-lg p-4 border-l-4 ${
                    req.status === 'accepted' ? 'border-green-500' :
                    req.status === 'rejected' ? 'border-red-500' :
                    req.status === 'alternatives_proposed' ? 'border-yellow-500' :
                    req.status === 'alternative_confirmed' ? 'border-blue-500' :
                    'border-purple-500'
                  }`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-semibold">{req.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            req.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                            req.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            req.status === 'alternatives_proposed' ? 'bg-yellow-500/20 text-yellow-400' :
                            req.status === 'alternative_confirmed' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-purple-500/20 text-purple-400'
                          }`}>
                            {req.status === 'pending' ? 'Neu' :
                             req.status === 'accepted' ? 'Angenommen' :
                             req.status === 'rejected' ? 'Abgelehnt' :
                             req.status === 'alternatives_proposed' ? 'Alternativen' :
                             req.status === 'alternative_confirmed' ? 'Bestätigt' :
                             req.status}
                          </span>
                        </div>
                        <p className="text-slate-400 text-sm mt-1">{req.email} · {req.phone}</p>
                        <p className="text-purple-400 text-sm font-medium mt-1">{req.app_name}</p>
                        <div className="flex items-center gap-2 mt-2 text-slate-300 text-sm">
                          <Calendar className="w-4 h-4" />
                          {new Date(req.requested_slot).toLocaleString('de-AT', { 
                            weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </div>
                        {req.message && <p className="text-slate-400 text-sm mt-2 italic">"{req.message}"</p>}
                        {req.selected_alternative && (
                          <p className="text-blue-400 text-sm mt-2">
                            <Check className="w-4 h-4 inline mr-1" />
                            Kunde hat gewählt: {new Date(req.selected_alternative).toLocaleString('de-AT')}
                          </p>
                        )}
                        {req.meeting_link && (
                          <p className="text-green-400 text-sm mt-2">
                            <Link2 className="w-4 h-4 inline mr-1" />
                            <a href={req.meeting_link} target="_blank" rel="noopener noreferrer" className="hover:underline">{req.meeting_link}</a>
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(req.created_at).toLocaleDateString('de-AT')}
                      </span>
                    </div>
                    
                    {/* Action Buttons */}
                    {(req.status === 'pending' || req.status === 'alternative_confirmed') && (
                      <div className="mt-4 pt-4 border-t border-slate-600">
                        <Button
                          size="sm"
                          onClick={() => setEditingDemoRequest(editingDemoRequest?.id === req.id ? null : {
                            ...req,
                            proposed_alternatives: req.proposed_alternatives || [],
                            meeting_link: req.meeting_link || '',
                            admin_notes: req.admin_notes || ''
                          })}
                          className="bg-slate-600 hover:bg-slate-500"
                        >
                          {editingDemoRequest?.id === req.id ? 'Schließen' : 'Bearbeiten'}
                        </Button>
                      </div>
                    )}
                    
                    {/* Edit Form */}
                    {editingDemoRequest?.id === req.id && (
                      <div className="mt-4 pt-4 border-t border-slate-600 space-y-4">
                        {/* Meeting Link */}
                        <div>
                          <label className="text-slate-300 text-sm block mb-1">Meeting-Link (Google Meet, Zoom, etc.)</label>
                          <Input 
                            value={editingDemoRequest.meeting_link}
                            onChange={(e) => setEditingDemoRequest({...editingDemoRequest, meeting_link: e.target.value})}
                            placeholder="https://meet.google.com/..."
                            className="bg-slate-700 border-slate-600 text-white"
                          />
                        </div>
                        
                        {/* Admin Notes */}
                        <div>
                          <label className="text-slate-300 text-sm block mb-1">Notizen für Kunden</label>
                          <Textarea 
                            value={editingDemoRequest.admin_notes}
                            onChange={(e) => setEditingDemoRequest({...editingDemoRequest, admin_notes: e.target.value})}
                            placeholder="Optionale Nachricht an den Kunden..."
                            className="bg-slate-700 border-slate-600 text-white"
                            rows={2}
                          />
                        </div>
                        
                        {/* Alternative Dates */}
                        {req.status === 'pending' && (
                          <div>
                            <label className="text-slate-300 text-sm block mb-2">Alternative Termine vorschlagen</label>
                            {editingDemoRequest.proposed_alternatives.map((alt, idx) => (
                              <div key={idx} className="flex items-center gap-2 mb-2">
                                <Input 
                                  type="datetime-local"
                                  value={alt.slice(0, 16)}
                                  onChange={(e) => {
                                    const newAlts = [...editingDemoRequest.proposed_alternatives];
                                    newAlts[idx] = new Date(e.target.value).toISOString();
                                    setEditingDemoRequest({...editingDemoRequest, proposed_alternatives: newAlts});
                                  }}
                                  className="bg-slate-700 border-slate-600 text-white flex-1"
                                />
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => {
                                    const newAlts = editingDemoRequest.proposed_alternatives.filter((_, i) => i !== idx);
                                    setEditingDemoRequest({...editingDemoRequest, proposed_alternatives: newAlts});
                                  }}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingDemoRequest({
                                ...editingDemoRequest, 
                                proposed_alternatives: [...editingDemoRequest.proposed_alternatives, new Date().toISOString()]
                              })}
                              className="border-slate-600 text-slate-300"
                            >
                              <Plus className="w-3 h-3 mr-1" /> Termin hinzufügen
                            </Button>
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2 flex-wrap">
                          {/* Accept Button */}
                          <Button
                            onClick={async () => {
                              if (!editingDemoRequest.meeting_link) {
                                return toast.error('Meeting-Link erforderlich');
                              }
                              try {
                                await axios.put(`${API}/admin/demo/requests/${req.id}`, {
                                  status: 'accepted',
                                  meeting_link: editingDemoRequest.meeting_link,
                                  admin_notes: editingDemoRequest.admin_notes
                                }, authHeaders());
                                toast.success('Anfrage angenommen - Kunde erhält E-Mail');
                                fetchDemoData();
                                setEditingDemoRequest(null);
                              } catch (err) {
                                toast.error('Fehler beim Speichern');
                              }
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4 mr-1" /> Annehmen
                          </Button>
                          
                          {/* Propose Alternatives Button */}
                          {req.status === 'pending' && editingDemoRequest.proposed_alternatives.length > 0 && (
                            <Button
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/admin/demo/requests/${req.id}`, {
                                    status: 'alternatives_proposed',
                                    proposed_alternatives: editingDemoRequest.proposed_alternatives,
                                    admin_notes: editingDemoRequest.admin_notes
                                  }, authHeaders());
                                  toast.success('Alternativen gesendet - Kunde erhält E-Mail');
                                  fetchDemoData();
                                  setEditingDemoRequest(null);
                                } catch (err) {
                                  toast.error('Fehler beim Speichern');
                                }
                              }}
                              className="bg-yellow-600 hover:bg-yellow-700"
                            >
                              <Clock className="w-4 h-4 mr-1" /> Alternativen senden
                            </Button>
                          )}
                          
                          {/* Reject Button */}
                          <Button
                            variant="destructive"
                            onClick={async () => {
                              if (!confirm('Anfrage wirklich ablehnen?')) return;
                              try {
                                await axios.put(`${API}/admin/demo/requests/${req.id}`, {
                                  status: 'rejected',
                                  admin_notes: editingDemoRequest.admin_notes
                                }, authHeaders());
                                toast.success('Anfrage abgelehnt - Kunde erhält E-Mail');
                                fetchDemoData();
                                setEditingDemoRequest(null);
                              } catch (err) {
                                toast.error('Fehler beim Speichern');
                              }
                            }}
                          >
                            <X className="w-4 h-4 mr-1" /> Ablehnen
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white">Kontaktanfragen</h2>
            {submissions.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Keine Anfragen</p>
            ) : submissions.map((sub) => (
              <div key={sub.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-black font-medium">{sub.name}</h3>
                    {sub.company && <p className="text-slate-400 text-sm">Firma: {sub.company}</p>}
                    <p className="text-slate-400 text-sm">{sub.email}</p>
                  </div>
                  <span className="text-xs text-gray-500">{new Date(sub.timestamp).toLocaleString('de-DE')}</span>
                </div>
                <div className="mt-2 flex gap-4 text-sm">
                  <span className="text-gray-600">Service: <span className="text-black">{sub.service}</span></span>
                  {sub.deadline && <span className="text-gray-600">Deadline: <span className="text-black">{sub.deadline}</span></span>}
                </div>
                <p className="text-gray-700 mt-4 whitespace-pre-wrap border-t border-gray-200 pt-4">{sub.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Admin Page
export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setChecking(false);
        return;
      }

      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        setIsAuthenticated(true);
        setChecking(false);
        return;
      } catch {}

      const token = getToken();
      if (!token) { setChecking(false); return; }
      try { 
        await axios.get(`${API}/admin/verify`, authHeaders()); 
        setIsAuthenticated(true); 
      } catch { 
        removeToken(); 
      }
      setChecking(false);
    };
    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    if (userData) setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    removeToken();
    setUser(null);
    setIsAuthenticated(false);
  };

  if (checking) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">Lädt...</div>;
  
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    return <><Toaster position="top-center" richColors /><AuthCallback onLogin={handleLogin} /></>;
  }
  
  if (!isAuthenticated) return <><Toaster position="top-center" richColors /><AdminLogin onLogin={handleLogin} /></>;
  return <AdminDashboard onLogout={handleLogout} user={user} />;
}
